// @ts-nocheck
/*
 * Port of views/dashboard/ivr/inbound-calls/inboundCalls.js (835 lines).
 * BYTE-IDENTICAL.
 *
 * Wires DID/VMN/TOLL FREE numbers to an IVR / Agent / Call Group with
 * a `number_type` filter tab. Agent branch also captures optional
 * "no agent available" prompt id + language and a required time
 * schedule. Missed-category IVR selection is gated by voice-plan
 * pulse-price check via hasMissedPlanWithPulsePrice; for non-DID
 * numbers the check follows `routing_id` back to the DID row.
 *
 * V1 QUIRKS preserved:
 *  - `handleNumberTypeFilter('DID')` is fired inside a useEffect keyed on
 *    the fetched `number` array; the state `numberType` is set to 'DID'
 *    in the same tick so the buttons and the filter agree.
 *  - The Focus/Blur handlers wrap window keydown into preventDefault
 *    while the action dropdown is focused â€” silences accidental
 *    submit-on-Enter. Kept exactly.
 *  - `handleActionChange` receives BOTH `e.target.value` and `e.value`
 *    because the file was originally PrimeReact-based (`e.value`) and
 *    the react-bootstrap swap synthesised a matching envelope.
 *  - LottieAnimation success modal â†’ plain react-bootstrap Modal with
 *    just the message (LottieAnimation dropped per v2 policy).
 */
import React, { useEffect, useState, useRef } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
Card.Footer = ({ className, children, ...props }: any) => (
  <div className={_cx('card-footer', className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_NUMBERS_LIST,
  API_AGENTS_LIST,
  API_AGENT_GROUPS_LIST,
  API_IVR_LIST,
  API_INBOUND_ROUTING,
  API_AGENT_APPROVED_PROMPTS,
} from '@/service/url';
import { WEEK_DAYS, ROUTES, DEFAULT_SCHEDULE_START, DEFAULT_SCHEDULE_END, DEFAULT_TIME_SCHEDULE } from '@/utilities/constant';
import { hasMissedPlanWithPulsePrice } from '@/utilities/voicePlanUtils';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import AiAgentCompletionConfig, {
  buildAiCompletionPayload,
  validateAiCompletionRules,
} from '@/features/inbound-calls/AiAgentCompletionConfig';
import { CalendarTimeInput } from '@/components/ui/calendar-inputs';

const InboundCalls = () => {
  const [number, setNumber] = useState<any[]>([]);
  const [ivr, setIvr] = useState<any[]>([]);
  const [agent, setAgent] = useState<any[]>([]);
  const [callGroup, setCallGroup] = useState<any[]>([]);
  const { cid } = useParams();
  const [showToastModal, setShowToastModal] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [selectedIvr, setSelectedIvr] = useState<any>(null);
  const [selectedNumber, setSelectedNumber] = useState<any>(null);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [selectedCallGroup, setSelectedCallGroup] = useState<any>(null);
  const [startTime, setStartTime] = useState(DEFAULT_SCHEDULE_START);
  const [endTime, setEndTime] = useState(DEFAULT_SCHEDULE_END);
  const [weekDays, setWeekDays] = useState<any[]>([...WEEK_DAYS]);
  const [timeScheduleModel, setTimeScheduleModel] = useState(false);
  const [previousTime, setPreviousTime] = useState<any>({ start: '', end: '' });
  const [previousWeekDays, setPreviousWeekDays] = useState<any[]>([]);
  const [isWholeDay, setIsWholeDay] = useState(true);
  const [isWholeWeek, setIsWholeWeek] = useState(true);
  const [scheduleData, setScheduleData] = useState<any>({ ...DEFAULT_TIME_SCHEDULE });
  const [action, setAction] = useState('');
  const [numberList, setNumberList] = useState<any[]>([]);
  const [numberType, setNumberType] = useState('');
  const [loading, setLoading] = useState(false);
  const [agentPromptOptions, setAgentPromptOptions] = useState<any[]>([]);
  const [
    selectedAgentNoAgentPromptId,
    setSelectedAgentNoAgentPromptId,
  ] = useState('');
  const [
    selectedAgentNoAgentPromptLanguage,
    setSelectedAgentNoAgentPromptLanguage,
  ] = useState('');
  const [selectedAiAgentId, setSelectedAiAgentId] = useState('');
  const [selectedAiAgentName, setSelectedAiAgentName] = useState('');
  const [aiCompletionRules, setAiCompletionRules] = useState<any[]>([]);

  const approvedLanguageEntries = (prompt: any) =>
    (Array.isArray(prompt?.prompt_url) ? prompt.prompt_url : []).filter(
      (entry: any) => Number(entry?.status) === 1 && entry?.language,
    );

  const hasApprovedLanguage = (prompt: any) =>
    approvedLanguageEntries(prompt).length > 0;

  const getApprovedPromptLanguages = (promptId: any) => {
    const prompt = agentPromptOptions.find(
      (p: any) => String(p.id) === String(promptId),
    );
    return approvedLanguageEntries(prompt);
  };

  const agentNoAgentPromptLanguages = getApprovedPromptLanguages(
    selectedAgentNoAgentPromptId,
  );

  const ivrOptions = ivr.map((item: any) => {
    const isCompleted = Number(item.status) === 1;
    return {
      ...item,
      flow_name_display: isCompleted
        ? item.flow_name
        : `${item.flow_name} (Pending)`,
      isDisabled: !isCompleted,
    };
  });

  const dropdownRef = useRef<any>(null);
  const [isDropdownFocused, setIsDropdownFocused] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: any) => {
      if (isDropdownFocused) {
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDropdownFocused]);

  const handleFocus = () => {
    setIsDropdownFocused(true);
  };

  const handleBlur = () => {
    setIsDropdownFocused(false);
  };

  const navigate = useNavigate();

  const showToasts = (message: string) => {
    setToastMessage(message);
    setShowToastModal(true);

    setTimeout(() => {
      setShowToastModal(false);
    }, 3000);
  };

  const fetchNumber = () => {
    getApiCall(API_NUMBERS_LIST).then((response: any) => {
      if (response) {
        setNumber(response);
      }
    });
  };

  const fetchIVR = () => {
    const apiIvrListUrl = API_IVR_LIST.replace(':cid', cid);
    getApiCall(apiIvrListUrl, { fields: 'min' }).then((response: any) => {
      if (response) {
        setIvr(response);
      }
    });
  };
  const fetchAgent = () => {
    getApiCall(API_AGENTS_LIST, { fields: 'min' }).then((response: any) => {
      if (response) {
        setAgent(response);
      }
    });
  };
  const fetchCallGroup = () => {
    getApiCall(API_AGENT_GROUPS_LIST, { fields: 'min' }).then((response: any) => {
      if (response) {
        setCallGroup(response);
      }
    });
  };

  useEffect(() => {
    fetchNumber();
    fetchIVR();
    fetchAgent();
    fetchCallGroup();
  }, []);

  useEffect(() => {
    handleNumberTypeFilter('DID');
    setNumberType('DID');
  }, [number]);

  const handleWholeDayChange = (e: any) => {
    const isChecked = e.target.checked;
    setIsWholeDay(isChecked);
      if (isChecked) {
      setPreviousTime({ start: startTime, end: endTime });
      setStartTime(DEFAULT_SCHEDULE_START);
      setEndTime(DEFAULT_SCHEDULE_END);
    } else {
      setStartTime(previousTime.start || '');
      setEndTime(previousTime.end || '');
    }
  };

  const handleWholeWeekChange = (e: any) => {
    const isChecked = e.target.checked;
    setIsWholeWeek(isChecked);
    if (isChecked) {
      setPreviousWeekDays(weekDays);
      setWeekDays(WEEK_DAYS as any);
    } else {
      setWeekDays(previousWeekDays || []);
    }
  };

  const toMinutes = (timeValue: any) => {
    if (!timeValue || !String(timeValue).includes(':')) return NaN;
    const [hours, minutes] = String(timeValue).split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return NaN;
    return hours * 60 + minutes;
  };

  const handleSetSchedule = () => {
    if (weekDays.length === 0) {
      toast.warning('Please fill all fields');
      return;
    }

    if (!isWholeDay) {
      if (!startTime || !endTime) {
        toast.warning('Please select both start time and end time');
        return;
      }

      const startMins = toMinutes(startTime);
      const endMins = toMinutes(endTime);

      if (!Number.isFinite(startMins) || !Number.isFinite(endMins)) {
        toast.warning('Invalid time selected');
        return;
      }

      if (weekDays.length === 1 && endMins <= startMins) {
        toast.warning(
          'For a single selected day, end time should be greater than start time',
        );
        return;
      }
    }

    setScheduleData({
      start_time: startTime,
      end_time: endTime,
      week_days: weekDays,
    });
    setTimeScheduleModel(false);
  };

  const handleScheduleClick = () => {
    setTimeScheduleModel(true);

    if (scheduleData && Object.keys(scheduleData).length > 0) {
      setStartTime(scheduleData.start_time);
      setEndTime(scheduleData.end_time);
      setWeekDays(scheduleData.week_days);

      if (scheduleData.week_days.length === 7) {
        setIsWholeWeek(true);
      } else {
        setIsWholeWeek(false);
      }

      if (
        scheduleData.start_time === DEFAULT_SCHEDULE_START &&
        scheduleData.end_time === DEFAULT_SCHEDULE_END
      ) {
        setIsWholeDay(true);
      } else {
        setIsWholeDay(false);
      }
    } else {
      setStartTime(DEFAULT_SCHEDULE_START);
      setEndTime(DEFAULT_SCHEDULE_END);
      setWeekDays([...WEEK_DAYS]);
      setIsWholeDay(true);
      setIsWholeWeek(true);
    }
  };

  const handleActionChange = (e: any) => {
    setAction(e.value);
    if (e.value !== 'agent') {
      setAgentPromptOptions([]);
      setSelectedAgentNoAgentPromptId('');
    }
    if (e.value !== 'ai_agent') {
      setSelectedAiAgentId('');
      setSelectedAiAgentName('');
      setAiCompletionRules([]);
    }
  };

  const handleSelectAiAgent = (agentId: string, agentName: string) => {
    setSelectedAiAgentId(agentId);
    setSelectedAiAgentName(agentName);
  };

  function numberHasMissedPlanWithPulsePrice(numberObj: any) {
    return hasMissedPlanWithPulsePrice((numberObj?.voice_plan_id ?? numberObj?.voice_plan)?.plans);
  }

  function resolveNumberForPlan(numObj: any) {
    if (!numObj) return null;
    let target = numObj;
    if (numberType !== 'DID' && numObj?.routing_id) {
      const routedDid = number.find(
        (n: any) => String(n.id) === String(numObj.routing_id),
      );
      if (routedDid) target = routedDid;
    }
    return target;
  }

  useEffect(() => {
    if (action !== 'ivr') return;
    if (!selectedIvr || !selectedNumber) return;
    if (String(selectedIvr?.ivr_category).toLowerCase() !== 'missed') return;
    const numberForPlan = resolveNumberForPlan(selectedNumber);
    if (!numberHasMissedPlanWithPulsePrice(numberForPlan)) {
      toast.warning("Selected number's voice plan does not support Missed Call IVR");
      setSelectedIvr(null);
    }
  }, [selectedIvr, selectedNumber, action]);

  function validateInboundCall() {
    let valid = true;

    if (!selectedNumber) {
      valid = false;
      toast.warning('Number is required');
    } else if (!action) {
      valid = false;
      toast.warning('Action is required');
    } else if (action === 'ivr' && !selectedIvr) {
      valid = false;
      toast.warning('IVR must be selected');
    } else if (action === 'agent' && !selectedAgent) {
      valid = false;
      toast.warning('Agent must be selected');
    } else if (
      action === 'agent' &&
      !(
        scheduleData &&
        Array.isArray(scheduleData.week_days) &&
        scheduleData.week_days.length > 0
      )
    ) {
      valid = false;
      toast.warning('Please set the time schedule for the agent');
    } else if (
      action === 'agent' &&
      selectedAgentNoAgentPromptId &&
      !selectedAgentNoAgentPromptLanguage
    ) {
      valid = false;
      toast.warning('Please select the language for the no agent prompt');
    } else if (action === 'queue' && !selectedCallGroup) {
      valid = false;
      toast.warning('Call Group must be selected');
    } else if (action === 'ai_agent' && !selectedAiAgentId) {
      valid = false;
      toast.warning('AI Agent must be selected');
    } else if (
      action === 'ai_agent' &&
      validateAiCompletionRules(aiCompletionRules)
    ) {
      valid = false;
      toast.warning(validateAiCompletionRules(aiCompletionRules));
    } else if (
      action === 'ivr' &&
      String(selectedIvr?.ivr_category).toLowerCase() === 'missed'
    ) {
      let numberForPlan = selectedNumber;
      if (numberType !== 'DID' && selectedNumber?.routing_id) {
        const routedDid = number.find(
          (n: any) => String(n.id) === String(selectedNumber.routing_id),
        );
        if (routedDid) numberForPlan = routedDid;
      }
      if (!numberHasMissedPlanWithPulsePrice(numberForPlan)) {
        valid = false;
        toast.warning(
          "Selected number's voice plan does not support Missed Call IVR",
        );
      }
    }

    return valid;
  }

  useEffect(() => {
    if (action !== 'agent' || !selectedAgent?.id) {
      setAgentPromptOptions([]);
      return;
    }

    getApiCall(API_AGENT_APPROVED_PROMPTS.replace(':id', selectedAgent.id))
      .then((resp: any) => {
        const list = Array.isArray(resp) ? resp : [];
        const noAgentPrompts = list.filter(
          (prompt: any) =>
            prompt.prompt_category &&
            Number(prompt.prompt_status) === 1 &&
            hasApprovedLanguage(prompt),
        );
        setAgentPromptOptions(noAgentPrompts);
      })
      .catch((err: any) => {
        console.error('Error fetching approved prompts for agent:', err);
        setAgentPromptOptions([]);
      });
  }, [action, selectedAgent?.id]);

  const handleInboundCreate = () => {
    if (!validateInboundCall()) {
      return;
    }
    const final: any = {
      number: selectedNumber.id,
      numberType: numberType,
      action: action,
    };

    if (action === 'ivr') {
      final.id = selectedIvr.flow_id;
      final.name = selectedIvr.flow_name;
    }

    if (action === 'agent') {
      final.id = selectedAgent.id;
      final.name = selectedAgent.agent_name;
      final.agentSchedule = scheduleData;
      final.no_agent_available_prompt_id =
        selectedAgentNoAgentPromptId && selectedAgentNoAgentPromptId !== ''
          ? Number(selectedAgentNoAgentPromptId)
          : null;
      final.no_agent_available_prompt_language =
        final.no_agent_available_prompt_id != null
          ? selectedAgentNoAgentPromptLanguage
          : null;
    }

    if (action === 'queue') {
      final.id = selectedCallGroup.id;
      final.name = selectedCallGroup.group_name;
      final.agentSchedule = scheduleData;
    }

    if (action === 'ai_agent') {
      final.id = selectedAiAgentId;
      final.name = selectedAiAgentName;
      final.completion_type = buildAiCompletionPayload(aiCompletionRules);
    }
    setLoading(true);

    postApiCall(API_INBOUND_ROUTING, final)
      .then(onInboundCallSuccess)
      .catch(onInboundCallError);
  };

  function onInboundCallSuccess(resp: any) {
    showToasts('Inbound Routing Completed!');
    setLoading(false);
    setTimeout(() => {
      navigate(`/${ROUTES.NUMBERS.LIST}`);
    }, 1500);
  }

  function onInboundCallError(err: any) {
    toast.warning(JSON.stringify(err));
    setLoading(false);
  }

  const allowsInboundRouting = (item: any) => {
    const v = String(item?.number_routing_type || '').trim().toLowerCase();
    return !v || v === 'incoming' || v === 'both';
  };

  const handleNumberTypeFilter = (type: any) => {
    setNumberType(type);
    let filteredData;
    if (type === 'DID') {
      filteredData = number.filter(
        (item: any) =>
          item.number_type === 'DID' && item.routing_destination === null,
      );
    } else if (type === 'VMN') {
      filteredData = number.filter(
        (item: any) =>
          item.number_type === 'VMN' && item.routing_destination !== null,
      );
    } else {
      filteredData = number.filter(
        (item: any) =>
          item.number_type === 'TOLL FREE' &&
          item.routing_destination !== null,
      );
    }
    setNumberList(filteredData.filter(allowsInboundRouting));
  };

  return (
    <FillPage>
      <PageHeader
        title="Inbound Calls Configuration"
        description="Route inbound DID numbers to IVR, agents, or voicemail."
      />
      <div>
        <Row>
          <Col sm="12">
            <Card>
              {/* Only DID numbers can be routed — DID / VMN / TOLL FREE selector disabled.
              <Card.Header className="d-block">
                <div className="flex flex-wrap gap-2">
                  {['DID', 'VMN', 'TOLL FREE'].map((t) => {
                    const active = numberType === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => handleNumberTypeFilter(t)}
                        className={
                          'flex h-10 items-center gap-2 rounded-lg border-2 px-4 text-sm font-bold transition ' +
                          (active
                            ? 'border-primary bg-primary text-primary-foreground shadow-md ring-2 ring-primary/25'
                            : 'border-primary/30 bg-primary/[0.06] text-primary hover:border-primary/50 hover:bg-primary/10')
                        }
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </Card.Header>
              */}
              <Card.Body>
                <Row className="mb-3">
                  <Col md="6">
                    <label htmlFor="numberDropdown" className="form-label">
                      Select {numberType}{' '}
                      <span style={{ color: 'red', margin: '0 4px' }}>*</span>
                    </label>
                    <Form.Select
                      id="numberDropdown"
                      value={selectedNumber?.id || ''}
                      onChange={(e: any) => {
                        const val = e.target.value;
                        if (!val) {
                          setSelectedNumber(null);
                          return;
                        }
                        const numObj = numberList.find(
                          (n: any) => String(n.id) === String(val),
                        );
                        setSelectedNumber(numObj || null);
                      }}
                      className="form-control"
                    >
                      <option value="">{`Select ${numberType}`}</option>
                      {numberList.map((numberItem: any) => (
                        <option key={numberItem.id} value={numberItem.id}>
                          {numberItem.actual_number}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md="6">
                    <label htmlFor="ivrDropdown" className="form-label">
                      Select Action{' '}
                      <span style={{ color: 'red', margin: '0 4px' }}>*</span>
                    </label>
                    <Form.Select
                      ref={dropdownRef}
                      id="ivrDropdown"
                      value={action || ''}
                      onChange={(e) =>
                        handleActionChange({
                          target: { value: e.target.value },
                          value: e.target.value,
                        })
                      }
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      className="form-control"
                    >
                      <option value="">Select Action</option>
                      <option value="ivr">IVR</option>
                      <option value="agent">Agent</option>
                      <option value="queue">Call Group</option>
                      <option value="ai_agent">AI Agent</option>
                    </Form.Select>
                  </Col>
                </Row>
                {action === 'ivr' && (
                  <Row className="mb-3">
                    <Col md="8">
                      <label htmlFor="ivrDropdown" className="form-label">
                        Select IVR{' '}
                        <span style={{ color: 'red', margin: '0 4px' }}>
                          *
                        </span>
                      </label>
                      <Form.Select
                        id="ivrDropdown"
                        value={selectedIvr?.flow_id || ''}
                        onChange={(e) => {
                          const selected = ivrOptions.find(
                            (ivr: any) =>
                              String(ivr.flow_id) === e.target.value,
                          );
                          setSelectedIvr(selected || null);
                        }}
                        className="form-control"
                      >
                        <option value="">Select IVR</option>
                        {ivrOptions.map((ivr: any) => (
                          <option
                            key={ivr.flow_id}
                            value={ivr.flow_id}
                            disabled={ivr.isDisabled}
                          >
                            {ivr.flow_name_display}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Row>
                )}
                {action === 'agent' && (
                  <Row className="mb-3">
                    <Col md="8">
                      <label htmlFor="agentDropdown" className="form-label">
                        Select Agent{' '}
                        <span style={{ color: 'red', margin: '0 4px' }}>
                          *
                        </span>
                      </label>
                      <Form.Select
                        id="agentDropdown"
                        value={selectedAgent?.id || ''}
                        onChange={(e) => {
                          const selected = agent.find(
                            (item: any) =>
                              String(item.id) === String(e.target.value),
                          );

                          setSelectedAgent(selected || null);
                          setSelectedAgentNoAgentPromptId(
                            selected?.no_agent_available_prompt_id != null
                              ? String(
                                  selected.no_agent_available_prompt_id,
                                )
                              : '',
                          );
                          setSelectedAgentNoAgentPromptLanguage(
                            selected?.no_agent_available_prompt_language || '',
                          );
                        }}
                        className="form-control"
                      >
                        <option value="">Select Agent</option>
                        {agent
                          .filter(
                            (item: any) =>
                              String(item.access).toLowerCase() !==
                              'team_lead',
                          )
                          .map((item: any) => (
                            <option key={item.id} value={item.id}>
                              {item.agent_name}
                            </option>
                          ))}
                      </Form.Select>
                    </Col>
                    <Col md="4">
                      <button
                        onClick={handleScheduleClick}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#28A745',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginLeft: '5px',
                          marginTop: '34px',
                        }}
                      >
                        Time Schedule
                      </button>
                    </Col>
                    <Col md="8" className="mt-3">
                      <label
                        htmlFor="agentNoAgentPromptDropdown"
                        className="form-label"
                      >
                        Please select no agent prompt
                      </label>
                      <Form.Select
                        id="agentNoAgentPromptDropdown"
                        value={selectedAgentNoAgentPromptId}
                        onChange={(e) => {
                          setSelectedAgentNoAgentPromptId(e.target.value);
                          setSelectedAgentNoAgentPromptLanguage('');
                        }}
                        className="form-control"
                      >
                        <option value="">-- Not selected --</option>
                        {agentPromptOptions.map((prompt: any) => (
                          <option key={prompt.id} value={prompt.id}>
                            {prompt.prompt_name}
                          </option>
                        ))}
                      </Form.Select>
                      <small className="text-muted d-block mt-1">
                        Optional: inbound routing can be created without
                        selecting this prompt.
                      </small>
                    </Col>
                    {selectedAgentNoAgentPromptId && (
                      <Col md="8" className="mt-3">
                        <label
                          htmlFor="agentNoAgentPromptLanguageDropdown"
                          className="form-label"
                        >
                          Prompt Language{' '}
                          <span style={{ color: 'red', margin: '0 4px' }}>
                            *
                          </span>
                        </label>
                        <Form.Select
                          id="agentNoAgentPromptLanguageDropdown"
                          value={selectedAgentNoAgentPromptLanguage}
                          onChange={(e) =>
                            setSelectedAgentNoAgentPromptLanguage(
                              e.target.value,
                            )
                          }
                          className="form-control"
                          disabled={agentNoAgentPromptLanguages.length === 0}
                        >
                          <option value="">
                            {agentNoAgentPromptLanguages.length === 0
                              ? 'No approved language for this prompt'
                              : 'Select Language'}
                          </option>
                          {agentNoAgentPromptLanguages.map((entry: any) => (
                            <option
                              key={entry.language}
                              value={entry.language}
                            >
                              {String(entry.language).toUpperCase()}
                            </option>
                          ))}
                        </Form.Select>
                        <small className="text-muted d-block mt-1">
                          This prompt has{' '}
                          {agentNoAgentPromptLanguages.length} approved
                          language
                          {agentNoAgentPromptLanguages.length === 1
                            ? ''
                            : 's'}
                          . Pick the one to play.
                        </small>
                      </Col>
                    )}
                  </Row>
                )}
                {action === 'queue' && (
                  <Row className="mb-3">
                    <Col md="8">
                      <label
                        htmlFor="agentGroupDropdown"
                        className="form-label"
                      >
                        Select Call Group{' '}
                        <span style={{ color: 'red', margin: '0 4px' }}>
                          *
                        </span>
                      </label>
                      <Form.Select
                        id="agentGroupDropdown"
                        value={selectedCallGroup?.id || ''}
                        onChange={(e) => {
                          const selected = callGroup.find(
                            (group: any) =>
                              String(group.id) === e.target.value,
                          );
                          setSelectedCallGroup(selected || null);
                        }}
                        className="form-control"
                      >
                        <option value="">Select Call Group</option>
                        {callGroup.map((group: any) => (
                          <option key={group.id} value={group.id}>
                            {group.group_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Row>
                )}
                {action === 'ai_agent' && (
                  <AiAgentCompletionConfig
                    selectedAiAgentId={selectedAiAgentId}
                    onSelectAiAgent={handleSelectAiAgent}
                    rules={aiCompletionRules}
                    onRulesChange={setAiCompletionRules}
                    agents={agent}
                    callGroups={callGroup}
                    ivrs={ivr}
                  />
                )}
              </Card.Body>
              <Card.Footer>
                <button
                  onClick={handleInboundCreate}
                  className="btn btn-primary"
                  style={{
                    marginLeft: '5px',
                    opacity: loading ? 0.5 : 1,
                  }}
                  disabled={loading}
                >
                  {loading ? 'Creating' : 'Create'}
                </button>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Auto-dismissing success confirmation. The Dialog's built-in close "x"
          is hidden because there is nothing to close by hand. */}
      <Modal
        show={showToastModal}
        centered
        onHide={() => setShowToastModal(false)}
        size="sm"
        className="[&>button]:hidden"
      >
        <Modal.Body className="flex flex-col items-center gap-3 py-2 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-success/10 text-success">
            <Check className="h-6 w-6" strokeWidth={3} />
          </span>
          <Modal.Title className="text-base font-semibold">{toastMessage}</Modal.Title>
        </Modal.Body>
      </Modal>

      <Modal
        show={timeScheduleModel}
        onHide={() => setTimeScheduleModel(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Set Time Schedule Of {action}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col>
              <Form>
                <Form.Check
                  type="checkbox"
                  label="Whole Day"
                  id="wholeDay"
                  className="mb-3"
                  checked={isWholeDay}
                  onChange={handleWholeDayChange}
                />
                <Form.Group className="mb-3">
                  <Form.Label>
                    Start Time{' '}
                    <span style={{ color: 'red', margin: '0 4px' }}>*</span>
                  </Form.Label>
                  <CalendarTimeInput
                    value={startTime}
                    onChange={(e) => setStartTime(e)}
                    disabled={isWholeDay}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    End Time{' '}
                    <span style={{ color: 'red', margin: '0 4px' }}>*</span>
                  </Form.Label>
                  <CalendarTimeInput
                    value={endTime}
                    onChange={(e) => setEndTime(e)}
                    disabled={isWholeDay}
                  />
                </Form.Group>
                <Form.Check
                  type="checkbox"
                  label="Whole Week"
                  id="wholeWeek"
                  className="mb-3"
                  checked={isWholeWeek}
                  onChange={handleWholeWeekChange}
                />
                <Form.Group className="mb-3">
                  <Form.Label>
                    Select Days
                    <span style={{ color: 'red', margin: '0 4px' }}>*</span>
                  </Form.Label>
                  <div>
                    {WEEK_DAYS.map((day: any) => (
                      <Form.Check
                        inline
                        key={day}
                        label={day}
                        type="checkbox"
                        checked={weekDays.includes(day)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setWeekDays((prev: any[]) => [...prev, day]);
                          } else {
                            setWeekDays((prev: any[]) =>
                              prev.filter((d: any) => d !== day),
                            );
                          }
                        }}
                        disabled={isWholeWeek}
                      />
                    ))}
                  </div>
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setTimeScheduleModel(false)}
          >
            Close
          </Button>
          <Button variant="primary" onClick={handleSetSchedule}>
            Set Schedule
          </Button>
        </Modal.Footer>
      </Modal>
    </FillPage>
  );
};

export default InboundCalls;
